import dayjs from "dayjs"
import advancedFormat from "dayjs/plugin/advancedFormat"
import iso from "iso-3166-1"
import camelCase from "lodash/camelCase"
import upperFirst from "lodash/upperFirst"

dayjs.extend(advancedFormat)

/**
 * Note: ordinals require dayjs advancedFormat plugin:
 *   import advancedFormat from "dayjs/plugin/advancedFormat"
 *   dayjs.extend(advancedFormat)
 */
export const DateFormat = {
  DateMonthDay: "MMM DD", // Abbreviated month with day. E.g. "Apr 16"
  DateShortSlash: "MM/DD/YYYY", // Date only, using slashes. E.g. "04/16/2024"
  DateShortDash: "YYYY-MM-DD", // Date only, using dashes. E.g. "2024-04-16"
  DateReadable: "MMM Do, YYYY", // Readable date, no time. E.g. "Apr 16th, 2024"
  DateDayAndMonth: "ddd, MMM DD", // Day of the week, and day of the month. E.g. "Tue, Apr 16"
  Datetime12Slash: "MM/DD/YYYY hh:mm A", // Date and 12-hour time. E.g. "04/16/2024 02:30 PM"
  Datetime24Dash: "YYYY-MM-DD HH:mm", // Date and 24-hour time with dashes. E.g. "2024-04-16 14:30"
}

export type IsoCountry = {
  country: string
  alpha2: string
}
export const getISOCountry = (countryCode: string): IsoCountry | undefined => {
  const country = iso.whereAlpha2(countryCode)
  return country ? formatISOCountry(country) : undefined
}

export const formatISOCountry = (country: IsoCountry): IsoCountry => {
  switch (country.alpha2.toUpperCase()) {
    case "GB": {
      return {
        ...country,
        country: "United Kingdom", // iso-3166-1 provides too long name for GB
      }
    }
    default: {
      return country
    }
  }
}

export function capitalize(word: string): string {
  return word.charAt(0).toUpperCase() + word.substring(1).toLowerCase()
}

/**
 * Transforms strings "readyToShip", "ReadyToShip" and "READY_TO_SHIP" to "Ready To Ship".
 *
 * Useful for converting enum keys to human-readable labels.
 *
 * @param input
 */
export const toSentenceCase = (
  input: string,
  {
    capitalizeOnlyFirstWord = false,
  }: { capitalizeOnlyFirstWord?: boolean } = {},
) => {
  if (typeof input !== "string") return ""

  const wordSeparated = input.includes("_")
    ? input.replace(/_+/g, " ")
    : input.toUpperCase() !== input
      ? input.replace(/([A-Z])/g, " $1")
      : input

  const words = wordSeparated
    .split(" ")
    .filter((word) => word.length > 0)
    .map((word) => word.trim().toLowerCase())

  if (capitalizeOnlyFirstWord) {
    return [capitalize(words[0]), ...words.slice(1)].join(" ")
  }

  return words.map(capitalize).join(" ").trim()
}

/**
 * Converts a string to PascalCase format.
 * Transforms strings like "hello world", "hello-world", "hello_world" to "HelloWorld".
 *
 * @param input - The string to convert to PascalCase
 * @returns The PascalCase version of the input, or the original input if not a string
 * @example
 * toPascalCase("hello world") // "HelloWorld"
 * toPascalCase("--container-orders--") // "ContainerOrders"
 * toPascalCase("__test_id_example__") // "TestIdExample"
 */
export const toPascalCase = (input: string) => {
  if (typeof input !== "string") return input
  return upperFirst(camelCase(input))
}

/**
 * Checks freetext state code for correspondence to New York state
 * TODO: Consider saving normalized two-symbol state code value
 * TODO: Consider replacing state text field with dropdown select
 * Examples (from production data)
 * @example ny
 * @example NY
 * @example new york
 * @example newyork
 * @example New york
 * @example Newyork
 * @param state - freetext state code
 */
export const isNewYorkState = (state: string | undefined | null) => {
  if (!state) return false

  const newYorkVariations = new Set(["ny", "new york", "newyork"])

  return newYorkVariations.has(state.trim().toLowerCase())
}

export const formatFileName = (filename: string): string => {
  const type = filename.split(".").pop()
  let name = filename
    .split(".")[0]
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "-")
  name = dayjs().format("YYYYMMDDHHmmss") + "-" + name
  if (type) {
    name = name + "." + type.toLowerCase()
  }
  return name
}
